/**
 * stereo-core.js — main-thread orchestrator for /stereo-pictures.
 *
 * Owns: manifest load, mode/source state, worker dispatch with seq-token,
 * gallery virtualizer (lazy thumbs via IntersectionObserver), big-canvas render,
 * Wiggle GIF assembly via vendored gif.js, save-as-PNG/GIF, ML depth pipeline.
 *
 * Test-only export at `window.StereoPictures.renderPresetToCanvas(id, mode)`
 * is what `tests/playwright/_stereo-presets-verify.mjs` uses to verify per-mode
 * pixel invariants on every preset.
 */

(function () {
    'use strict';

    var SLUG = 'stereo-pictures';

    /* ──────────────────────────────────────────────────────────────
       Two-worker dispatch — active canvas vs thumb backlog
       ──────────────────────────────────────────────────────────── */
    /* Background: an earlier single-worker design queued ALL thumb renders
     * (~100) plus active canvas renders into one worker. When the user
     * switched mode or moved a slider, paintGallery() flooded the queue with
     * 100 thumb messages; their subsequent card click landed at message
     * #101 and waited ~3 s for the worker to catch up. Surfaced as bug:
     * "I click presets but the big canvas stays on LOVE forever".
     * Splitting into two workers (one for the active canvas, one for thumbs)
     * gives user clicks an always-free pipe; thumbnails chug in background. */

    var _seq = 0;
    var _liveSeqByMode = {};  /* active canvas only — drop stale on mode-switch */

    var _activeWorker = null;
    var _thumbsWorker = null;

    function makeWorker() {
        // COEP-isolated pages block worker scripts served without a COEP header.
        // This worker spawns at PAGE INIT (thumbs) — on a first-ever visit the SW
        // hasn't claimed the page yet, so the SW header-injection can't cover it:
        // use the header-carrying /workers/ route when isolated (server headers,
        // no SW dependency). Static path stays for everyone else.
        var _iso = (typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated);
        var _base = _iso ? '/workers/stereo-worker' : (window.__stereoWorkerUrl || '/js/stereo-worker.js');
        var w = new Worker(_base + '?v=' + (window.__stereoWorkerVer || ''));
        var pending = new Map();
        w.onmessage = function (e) {
            var d = e.data;
            var p = pending.get(d.seq);
            if (!p) return;
            pending.delete(d.seq);
            if (d.type === 'render-error') p.reject(new Error(d.error));
            else                            p.resolve(d);
        };
        w.onerror = function (err) {
            pending.forEach(function (p) { p.reject(err); });
            pending.clear();
        };
        return { worker: w, pending: pending };
    }

    function getActiveWorker() { if (!_activeWorker) _activeWorker = makeWorker(); return _activeWorker; }
    function getThumbsWorker() { if (!_thumbsWorker) _thumbsWorker = makeWorker(); return _thumbsWorker; }

    /**
     * workerRender — dispatch a render and return the response.
     *
     * @param {string} mode - 'magicEye' | 'anaglyph' | 'crossEye' | 'wiggle'
     * @param {object} source - {kind, ...}
     * @param {object} opts - render opts
     * @param {boolean} trackActive - if true, response is dropped when a newer
     *        same-mode active render overtakes it (avoids stale UI).
     * @param {string} target - 'active' (default, user-facing canvas) or
     *        'thumb' (gallery virtualizer). Two separate workers keep the
     *        active pipe responsive even when the thumb backlog is deep.
     */
    function workerRender(mode, source, opts, trackActive, target) {
        var ctx = (target === 'thumb') ? getThumbsWorker() : getActiveWorker();
        _seq++;
        var seq = _seq;
        if (trackActive) _liveSeqByMode[mode] = seq;
        var p = new Promise(function (resolve, reject) {
            ctx.pending.set(seq, { resolve: resolve, reject: reject });
        });
        ctx.worker.postMessage({ type: 'render', seq: seq, mode: mode, source: source, opts: opts });
        return p.then(function (resp) {
            if (trackActive && _liveSeqByMode[mode] !== seq) {
                if (resp.bitmap)      try { resp.bitmap.close(); } catch (e) {}
                if (resp.leftBitmap)  try { resp.leftBitmap.close(); } catch (e) {}
                if (resp.rightBitmap) try { resp.rightBitmap.close(); } catch (e) {}
                throw Object.assign(new Error('stale'), { __stereoStale: true });
            }
            return resp;
        });
    }

    /* Pattern setters need to broadcast to BOTH workers — both use it. */
    function broadcast(msg) {
        getActiveWorker().worker.postMessage(msg);
        getThumbsWorker().worker.postMessage(msg);
    }

    /* ──────────────────────────────────────────────────────────────
       Manifest
       ──────────────────────────────────────────────────────────── */

    var _manifestPromise = null;
    function loadManifest() {
        if (_manifestPromise) return _manifestPromise;
        _manifestPromise = fetch('/data/stereo/presets.json', { credentials: 'omit' })
            .then(function (r) {
                if (!r.ok) throw new Error('manifest HTTP ' + r.status);
                return r.json();
            })
            .then(function (j) {
                if (!j || !Array.isArray(j.presets)) throw new Error('manifest invalid');
                return j;
            });
        return _manifestPromise;
    }

    /* ──────────────────────────────────────────────────────────────
       Public API + gallery virtualizer
       ──────────────────────────────────────────────────────────── */

    var StereoPictures = {
        ready: false,
        manifest: null,
        mode: 'magicEye',                 // 'magicEye' | 'anaglyph' | 'crossEye' | 'wiggle'
        source: { kind: 'preset', presetId: 't-love' },
        opts: {
            width: 800,
            height: 600,
            eyeWidthPx: 128,
            maxShiftPx: 12,
            patternSeed: 1,
            desaturate: true,
        },
        thumbCache: new Map(),

        async init(rootEl) {
            var data = await loadManifest();
            this.manifest = data;
            this.ready = true;
            buildGallery(rootEl, data, this);
            await this.renderActive();
            return this;
        },

        async setMode(newMode) {
            if (this.mode === newMode) return;
            this.mode = newMode;
            this.thumbCache.clear(); /* per-mode cache; cheap to refill on visibility */
            paintGallery(this);
            await this.renderActive();
        },

        async setSource(src) {
            this.source = src;
            await this.renderActive();
        },

        async setEyeWidth(px) {
            this.opts.eyeWidthPx = Math.max(60, Math.min(220, px | 0));
            this.thumbCache.clear();
            paintGallery(this);
            await this.renderActive();
        },

        /* Custom pattern tile (Magic Eye only). User-supplied ImageBitmap
         * becomes the repeating motif of the autostereogram, replacing the
         * default coloured noise. Bitmap is held on the worker side until
         * cleared, so the main thread doesn't have to re-transfer it. */
        async setPattern(bitmap) {
            broadcast({ type: 'set-pattern', bitmap: bitmap });
            this.thumbCache.clear();
            paintGallery(this);
            await this.renderActive();
        },
        async clearPattern() {
            broadcast({ type: 'clear-pattern' });
            this.thumbCache.clear();
            paintGallery(this);
            await this.renderActive();
        },

        /**
         * renderPresetToCanvas — used by the verifier (per-mode invariants).
         * Returns an HTMLCanvasElement with the freshly rendered bitmap drawn.
         */
        async renderPresetToCanvas(presetId, mode) {
            var preset = this.manifest.presets.find(function (p) { return p.id === presetId; });
            if (!preset) throw new Error('preset not found: ' + presetId);
            var resp = await workerRender(mode, { kind: 'preset', preset: localizePreset(preset) }, this.opts, false);
            return responseToCanvas(resp, mode, this.opts);
        },

        async renderActive() {
            var bigCanvas = document.getElementById('spOutCanvas');
            if (!bigCanvas) return;
            /* No-op when the manifest hasn't loaded yet — happens when blade JS
             * fires saved-mode click() before SP.init() resolves. The click
             * handler still updates the visual mode tabs; renderActive() at the
             * end of init() will paint the correct mode + source. */
            if (!this.manifest || !Array.isArray(this.manifest.presets)) return;
            var src = this.source;
            var preset = (src.kind === 'preset')
                ? this.manifest.presets.find(function (p) { return p.id === src.presetId; })
                : null;
            var workerSource;
            if (this.source.kind === 'preset' && preset)         workerSource = { kind: 'preset',       preset: localizePreset(preset) };
            else if (this.source.kind === 'depthBitmap')         workerSource = { kind: 'depthBitmap',  bitmap: this.source.bitmap, width: this.source.width, height: this.source.height };
            else if (this.source.kind === 'colorAndDepth')       workerSource = { kind: 'colorAndDepth', colorBitmap: this.source.colorBitmap, depth: this.source.depth, width: this.source.width, height: this.source.height };
            else if (this.source.kind === 'text')                workerSource = { kind: 'text',         text: this.source.text, font: this.source.font || 'Impact' };
            else throw new Error('unknown source.kind');
            try {
                var resp = await workerRender(this.mode, workerSource, this.opts, true);
                drawResponseToCanvas(bigCanvas, resp, this.mode, this.opts);
                if (window.__ae) window.__ae('stereo_render', { mode: this.mode, source: this.source.kind });
            } catch (err) {
                if (err && err.__stereoStale) return;
                if (window.__handleError) window.__handleError(err, 'stereo_render');
            }
        },

        async exportCurrent() {
            var bigCanvas = document.getElementById('spOutCanvas');
            if (!bigCanvas) return;
            if (this.mode === 'wiggle') return this.exportWiggleGif();
            return new Promise(function (resolve) {
                bigCanvas.toBlob(function (blob) {
                    if (!blob) return resolve();
                    triggerDownload(blob, 'stereo-' + this.mode + '-' + Date.now() + '.png');
                    if (window.__ae) window.__ae('stereo_download', { mode: this.mode, bytes: blob.size });
                    resolve();
                }.bind(this), 'image/png');
            }.bind(this));
        },

        async exportWiggleGif() {
            /* Re-run the worker request to capture both frames as ImageBitmaps
             * (the on-screen canvas only shows the current frame in the loop). */
            var workerSource = sourceToWorkerSource(this.source, this.manifest);
            var resp = await workerRender('wiggle', workerSource, this.opts, false);
            var W = this.opts.width, H = this.opts.height;
            var leftCanvas  = bitmapToCanvas(resp.leftBitmap,  W, H);
            var rightCanvas = bitmapToCanvas(resp.rightBitmap, W, H);
            return encodeWiggleGif(leftCanvas, rightCanvas).then(function (blob) {
                triggerDownload(blob, 'wiggle-' + Date.now() + '.gif');
                if (window.__ae) window.__ae('stereo_download', { mode: 'wiggle', bytes: blob.size });
            });
        },
    };

    /* ──────────────────────────────────────────────────────────────
       Gallery virtualizer
       ──────────────────────────────────────────────────────────── */

    function buildGallery(rootEl, data, app) {
        rootEl.innerHTML = '';
        var grid = document.createElement('div');
        grid.className = 'sp-grid';
        rootEl.appendChild(grid);
        data.presets.forEach(function (preset) {
            var card = document.createElement('button');
            card.type = 'button';
            card.className = 'sp-card';
            card.setAttribute('data-id', preset.id);
            /* Card label resolution chain:
             *   1. text-kind preset → __stereoLocaleTexts (localized word)
             *   2. otherwise → __stereoLocaleLabels[id] (localized noun)
             *   3. final fallback → id stem (English) */
            var label;
            if (preset.kind === 'text' && window.__stereoLocaleTexts && window.__stereoLocaleTexts[preset.id]) {
                label = window.__stereoLocaleTexts[preset.id];
            } else if (preset.kind === 'text') {
                label = preset.text;
            } else if (window.__stereoLocaleLabels && window.__stereoLocaleLabels[preset.id]) {
                label = window.__stereoLocaleLabels[preset.id];
            } else {
                label = preset.id.replace(/^[ts]-|^shape-/, '').replace(/-/g, ' ');
            }
            card.setAttribute('aria-label', label);
            card.innerHTML = '<div class="sp-card-thumb"><div class="sp-card-spinner" aria-hidden="true"></div></div>'
                + '<div class="sp-card-label">' + escapeHtml(label) + '</div>';
            card.addEventListener('click', function () {
                app.setSource({ kind: 'preset', presetId: preset.id });
                if (window.__ae) window.__ae('stereo_preset_click', { id: preset.id, mode: app.mode });
                document.querySelectorAll('.sp-card.is-active').forEach(function (n) { n.classList.remove('is-active'); });
                card.classList.add('is-active');
            });
            grid.appendChild(card);
        });

        /* IntersectionObserver — kept alive for the lifetime of the page (no
         * unobserve), so subsequent mode/eye-width changes can invalidate the
         * cache and let cards re-paint lazily as they come back into view.
         * 200 ms slack on rootMargin so fast scroll doesn't render-then-evict. */
        if ('IntersectionObserver' in window) {
            var io = new IntersectionObserver(function (entries) {
                entries.forEach(function (e) {
                    if (!e.isIntersecting) return;
                    paintCard(e.target, app);
                });
            }, { rootMargin: '200px 0px' });
            grid.querySelectorAll('.sp-card').forEach(function (n) { io.observe(n); });
            grid.__sp_io = io;
        } else {
            grid.querySelectorAll('.sp-card').forEach(function (n) { paintCard(n, app); });
        }
    }

    /**
     * Cache-invalidate + repaint only currently-visible cards. Cards outside
     * the viewport stay as spinner placeholders; the IO observer triggers
     * paintCard for them when they scroll into view. Avoids the previous
     * "100-message worker flood on every mode/slider change" stampede.
     */
    function paintGallery(app) {
        if (!app.manifest) return; /* init() not done yet — gallery DOM doesn't exist */
        var grid = document.querySelector('.sp-grid');
        if (!grid) return;
        var vh = window.innerHeight || document.documentElement.clientHeight;
        grid.querySelectorAll('.sp-card').forEach(function (card) {
            var thumb = card.querySelector('.sp-card-thumb');
            if (thumb) thumb.innerHTML = '<div class="sp-card-spinner" aria-hidden="true"></div>';
            var rect = card.getBoundingClientRect();
            var visible = rect.top < vh + 200 && rect.bottom > -200;
            if (visible) paintCard(card, app);
        });
    }

    function paintCard(card, app) {
        if (!app.manifest) return;
        var id = card.getAttribute('data-id');
        var key = id + '|' + app.mode + '|' + app.opts.eyeWidthPx;
        if (app.thumbCache.has(key)) {
            renderThumbBitmapToCard(card, app.thumbCache.get(key));
            return;
        }
        var preset = app.manifest.presets.find(function (p) { return p.id === id; });
        if (!preset) return;
        /* Thumbs render at lower res for speed. */
        var thumbOpts = Object.assign({}, app.opts, { width: 240, height: 180, eyeWidthPx: Math.round(app.opts.eyeWidthPx * 0.6) });
        workerRender(app.mode, { kind: 'preset', preset: localizePreset(preset) }, thumbOpts, false, 'thumb').then(function (resp) {
            var bm = resp.bitmap || resp.leftBitmap; /* wiggle: just show first frame */
            if (!bm) return;
            app.thumbCache.set(key, bm);
            renderThumbBitmapToCard(card, bm);
        }).catch(function (err) {
            if (err && err.__stereoStale) return;
            console.warn('[stereo] thumb render failed', id, err);
        });
    }

    function renderThumbBitmapToCard(card, bm) {
        var thumb = card.querySelector('.sp-card-thumb');
        if (!thumb) return;
        thumb.innerHTML = '';
        var c = document.createElement('canvas');
        c.width = bm.width;
        c.height = bm.height;
        c.getContext('2d').drawImage(bm, 0, 0);
        thumb.appendChild(c);
    }

    /* ──────────────────────────────────────────────────────────────
       Helpers
       ──────────────────────────────────────────────────────────── */

    /**
     * Apply locale-specific text override to a text-kind preset before sending
     * to the worker. The blade exposes window.__stereoLocaleTexts (a map of
     * preset-id → localized short word per current locale). For non-text
     * presets, returns the original unchanged.
     */
    function localizePreset(p) {
        if (!p || p.kind !== 'text') return p;
        var overrides = window.__stereoLocaleTexts || {};
        var loc = overrides[p.id];
        if (typeof loc !== 'string' || loc === '') return p;
        return Object.assign({}, p, { text: loc });
    }

    function sourceToWorkerSource(src, manifest) {
        if (src.kind === 'preset') {
            var p = manifest.presets.find(function (x) { return x.id === src.presetId; });
            return { kind: 'preset', preset: localizePreset(p) };
        }
        if (src.kind === 'text')          return { kind: 'text', text: src.text, font: src.font || 'Impact' };
        if (src.kind === 'depthBitmap')   return { kind: 'depthBitmap', bitmap: src.bitmap, width: src.width, height: src.height };
        if (src.kind === 'colorAndDepth') return { kind: 'colorAndDepth', colorBitmap: src.colorBitmap, depth: src.depth, width: src.width, height: src.height };
        throw new Error('unknown source.kind');
    }

    function bitmapToCanvas(bm, W, H) {
        var c = document.createElement('canvas');
        c.width  = W || bm.width;
        c.height = H || bm.height;
        c.getContext('2d').drawImage(bm, 0, 0);
        return c;
    }

    function drawResponseToCanvas(canvas, resp, mode, opts) {
        if (mode === 'crossEye') {
            canvas.width  = resp.width;
            canvas.height = resp.height;
            canvas.getContext('2d').drawImage(resp.bitmap, 0, 0);
            return;
        }
        if (mode === 'wiggle') {
            canvas.width  = opts.width;
            canvas.height = opts.height;
            /* Loop two frames at 120 ms via setInterval; cancel on next render. */
            stopWiggleLoop(canvas);
            var ctx = canvas.getContext('2d');
            var frames = [resp.leftBitmap, resp.rightBitmap];
            var idx = 0;
            ctx.drawImage(frames[0], 0, 0);
            var iv = setInterval(function () {
                idx = (idx + 1) % 2;
                ctx.drawImage(frames[idx], 0, 0);
            }, 120);
            canvas.__wiggleInterval = iv;
            canvas.__wiggleFrames   = frames;
            return;
        }
        canvas.width  = opts.width;
        canvas.height = opts.height;
        canvas.getContext('2d').drawImage(resp.bitmap, 0, 0);
    }

    function responseToCanvas(resp, mode, opts) {
        var c = document.createElement('canvas');
        if (mode === 'crossEye')   { c.width = resp.width;  c.height = resp.height; c.getContext('2d').drawImage(resp.bitmap, 0, 0); return c; }
        if (mode === 'wiggle')     { c.width = opts.width;  c.height = opts.height; c.getContext('2d').drawImage(resp.leftBitmap, 0, 0); c.__rightBitmap = resp.rightBitmap; return c; }
        c.width = opts.width; c.height = opts.height;
        c.getContext('2d').drawImage(resp.bitmap, 0, 0);
        return c;
    }

    function stopWiggleLoop(canvas) {
        if (canvas.__wiggleInterval) { clearInterval(canvas.__wiggleInterval); canvas.__wiggleInterval = null; }
        if (canvas.__wiggleFrames)   { canvas.__wiggleFrames.forEach(function (b) { try { b.close(); } catch (e) {} }); canvas.__wiggleFrames = null; }
    }

    function triggerDownload(blob, filename) {
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
    }

    function escapeHtml(s) {
        return String(s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
    }

    /* ──────────────────────────────────────────────────────────────
       Wiggle GIF assembly (uses vendored gif.js + gif.worker.js)
       ──────────────────────────────────────────────────────────── */

    function encodeWiggleGif(leftCanvas, rightCanvas) {
        return new Promise(function (resolve, reject) {
            if (typeof window.GIF !== 'function') return reject(new Error('GIF library not loaded'));
            var gif = new window.GIF({
                workers: 2,
                quality: 10,
                width:  leftCanvas.width,
                height: leftCanvas.height,
                workerScript: '/vendor/gif.js/gif.worker.js',
                transparent: null,
            });
            gif.addFrame(leftCanvas,  { copy: true, delay: 80 });
            gif.addFrame(rightCanvas, { copy: true, delay: 80 });
            gif.on('finished', function (blob) { resolve(blob); });
            gif.on('abort', function () { reject(new Error('gif abort')); });
            gif.render();
        });
    }

    /* ──────────────────────────────────────────────────────────────
       Public exports
       ──────────────────────────────────────────────────────────── */

    window.StereoPictures = StereoPictures;

})();
